import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import {
  SegmentTab,
  SEGMENT_DETAILS,
} from 'components/SegmentDetails/constants'
import SampleCustomers from 'components/SegmentDetails/SampleCustomers'
import SegmentRules from 'components/SegmentDetails/SegmentRules'
import SegmentSummary from 'components/SegmentDetails/SegmentSummary'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import { Fields as TargetSegment } from 'models/TargetSegment'
import { useState } from 'react'
import Nav from 'react-bootstrap-five/Nav'
import Tab from 'react-bootstrap-five/Tab'

type SegmentDetailsProps = {
  // Undefined while the segment is still being fetched: the tabs render
  // immediately and only the content region shows a spinner, so the frame
  // doesn't reflow when the data lands.
  segment?: TargetSegment
  // A refetch of an already-cached segment still holds the previous copy, so
  // `loading` — not just a missing `segment` — decides whether the panes render.
  loading?: boolean
  className?: string
  id?: string
}

const { TAB_KEYS } = SEGMENT_DETAILS

const t = buildTranslate('segments.details')

const SegmentDetails = ({
  segment,
  loading = false,
  className = '',
  id,
}: SegmentDetailsProps) => {
  const [activeTab, setActiveTab] = useState<SegmentTab>(TAB_KEYS.SUMMARY)
  const [css] = useStyletron()

  const segmentDetailsClass = `segment-details ${className}`.trim()

  return (
    <div className={segmentDetailsClass}>
      <Tab.Container
        id={id}
        activeKey={activeTab}
        transition={false}
        onSelect={(tab: string | null) => {
          if (!tab) return
          setActiveTab(tab as SegmentTab)
        }}
      >
        <Nav
          className="nav-tabs"
          role="tablist"
          aria-label={t('tabs.navigation_label')}
        >
          <Nav.Item>
            <Nav.Link
              eventKey={TAB_KEYS.SUMMARY}
              className={css({ backgroundColor: 'transparent !important' })}
              role="tab"
              aria-controls={`${id}-${TAB_KEYS.SUMMARY}-panel`}
              aria-selected={activeTab === TAB_KEYS.SUMMARY}
            >
              {t('tabs.summary')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey={TAB_KEYS.SEGMENT_RULES}
              className={css({ backgroundColor: 'transparent !important' })}
              role="tab"
              aria-controls={`${id}-${TAB_KEYS.SEGMENT_RULES}-panel`}
              aria-selected={activeTab === TAB_KEYS.SEGMENT_RULES}
            >
              {t('tabs.segment_rules')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey={TAB_KEYS.SAMPLE_CUSTOMERS}
              className={css({ backgroundColor: 'transparent !important' })}
              role="tab"
              aria-controls={`${id}-${TAB_KEYS.SAMPLE_CUSTOMERS}-panel`}
              aria-selected={activeTab === TAB_KEYS.SAMPLE_CUSTOMERS}
            >
              {t('tabs.sample_customers')}
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          {segment && !loading ? (
            <>
              <SegmentSummary segment={segment} tabId={id} />
              <SegmentRules segment={segment} tabId={id} />
              <SampleCustomers segmentId={String(segment.id)} tabId={id} />
            </>
          ) : (
            <Switch condition={loading}>
              <div role="status" aria-label={t('loading')}>
                <Spinner
                  isLoading
                  size="2x"
                  className="d-flex justify-content-center pt-xl"
                />
              </div>
            </Switch>
          )}
        </Tab.Content>
      </Tab.Container>
    </div>
  )
}

export default SegmentDetails
